import { Flex, SimpleGrid, TextInput } from "@mantine/core";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import { last } from "lodash";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useMemo, useState } from "react";

import { useUpdateCollateralStatus } from "../../../api/hooks/collateral/useUpdateCollateralStatus.ts";
import { useCompanyOptions } from "../../../api/hooks/customer/useCompanyOptions.ts";
import { useApproveTransactionHistory } from "../../../api/hooks/transactionHistory/useApproveTransactionHistory.ts";
import { useGetTransactionHistoryList } from "../../../api/hooks/transactionHistory/useGetTransactionHistoryList.ts";
import { useRejectTransactionHistory } from "../../../api/hooks/transactionHistory/useRejectTransactionHistory.ts";
import { useUpdateTransactionHistory } from "../../../api/hooks/transactionHistory/useUpdateTransactionHistory.ts";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal.tsx";
import ModalWithNotes from "../../../components/ModalWithNotes/ModalWithNotes.tsx";
import { Select } from "../../../components/Select.tsx";
import Table from "../../../components/Table/Table.tsx";
import { SortDirection } from "../../../components/Table/types.ts";
import { boolSelectorOptions } from "../../../constants.ts";
import {
  OrderBy,
  TransactionHistoryOrderBy,
  TransactionHistoryOrderStatus,
  UpdateCollateralInput,
  UpdateTransactionHistoryInput,
} from "../../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../../hooks/useMutationNotificationWrapper.tsx";
import { Customer } from "../../../types/customer.ts";
import { LimitRequestConditionPrecedent } from "../../../types/limitRequestConditionPrecedent.ts";
import { LimitRequestException } from "../../../types/limitRequestException.ts";
import {
  orderStatusOptions,
  paymentMethodOptions,
  paymentTypeOptions,
  TransactionHistory,
} from "../../../types/transactionHistory.ts";
import { stringToBoolean } from "../../../utils/boolean.ts";
import {
  getSalesNameOptions,
  getSalesTeamOptions,
} from "../../../utils/company.ts";
import { toCurrencyPrice } from "../../../utils/number.ts";
import { getPagesCount } from "../../../utils/pagination.ts";
import CollateralModal from "./CollateralModal.tsx";
import ConditionPrecedentsModal from "./ConditionPrecedentsModal/ConditionPrecedentsModal.tsx";
import ExceptionModal from "./ExceptionModal/ExceptionModal.tsx";
import { useTransactionHistoryTableData } from "./hooks/useTransactionHistoryTableData.tsx";
import { ModalType, TransactionHistoryFiltersValue } from "./types.ts";
import {
  getTransactionHistoryApiFilter,
  getTransactionHistoryRequestApiSort,
} from "./utils.ts";

interface TransactionHistoryListProps {
  customer: Pick<Customer, "id" | "name"> | null | undefined;
  canEdit?: boolean;
}

const TransactionHistoryList = ({
  customer,
  canEdit = true,
}: TransactionHistoryListProps) => {
  const [filter, setFilter] = useState<TransactionHistoryFiltersValue>({});
  const [quotationNoFilter, setQuotationNoFilter] = useDebouncedState("", 500);

  const apiFilter = useMemo(() => {
    return getTransactionHistoryApiFilter({
      ...filter,
      quotationNo: quotationNoFilter,
    });
  }, [filter, quotationNoFilter]);
  const [page, setPage] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<TransactionHistoryOrderBy>({
    orderDate: OrderBy.Desc,
  });

  const [selectedLimitRequestId, setSelectedLimitRequestId] = useState<
    number | null | undefined
  >(null);
  const [selectedTransactionHistory, setSelectedTransactionHistory] = useState<
    TransactionHistory | null | undefined
  >(null);
  const selectedCollateral = last(selectedTransactionHistory?.collateral);

  const [selectedConditionPrecedents, setSelectedConditionalPrecedents] =
    useState<LimitRequestConditionPrecedent[] | null | undefined>(null);
  const [selectedExceptions, setSelectedExceptions] = useState<
    LimitRequestException[] | null | undefined
  >(null);

  const [
    conditionPrecedentEditModalOpened,
    {
      open: conditionPrecedenceEditModalOpen,
      close: conditionPrecedenceEditModalClose,
    },
  ] = useDisclosure(false);
  const showConditionPrecedentEditModal =
    conditionPrecedentEditModalOpened &&
    selectedLimitRequestId &&
    selectedConditionPrecedents;

  const [
    rejectConfirmationModalOpened,
    { open: openRejectConfirmationModal, close: closeRejectConfirmationModal },
  ] = useDisclosure(false);
  const showRejectConfirmationModal =
    rejectConfirmationModalOpened && selectedTransactionHistory;

  const [
    approveConfirmationModalOpened,
    {
      open: openApproveConfirmationModal,
      close: closeApproveConfirmationModal,
    },
  ] = useDisclosure(false);
  const showApproveConfirmationModal =
    approveConfirmationModalOpened && selectedTransactionHistory;

  const [
    exceptionEditModalOpened,
    { open: exceptionEditModalOpen, close: exceptionEditModalClose },
  ] = useDisclosure(false);
  const showExceptionEditModal =
    exceptionEditModalOpened && selectedLimitRequestId && selectedExceptions;

  const [
    collateralModalOpened,
    { open: collateralModalOpen, close: collateralModalClose },
  ] = useDisclosure(false);
  const showCollateralModal =
    collateralModalOpened && selectedTransactionHistory;

  const [{ data, fetching }, refresh] = useGetTransactionHistoryList({
    filter: { ...apiFilter, customerId: { equals: customer?.id } },
    orderBy,
    page,
    pause: !customer?.id,
  });

  const [{ data: companiesData }] = useCompanyOptions();
  const companies = companiesData?.customers?.data;

  const [, approveTransactionHistory] = useMutationNotificationWrapper(
    useApproveTransactionHistory(),
    {
      success: {
        message: "Transaction History has been successfully approved.",
      },
    }
  );
  const [, rejectTransactionHistory] = useMutationNotificationWrapper(
    useRejectTransactionHistory(),
    {
      success: {
        message: "Transaction History has been successfully rejected.",
      },
    }
  );

  const [, updateTransactionHistory] = useMutationNotificationWrapper(
    useUpdateTransactionHistory(),
    {
      success: {
        message: "Transaction History has been successfully updated.",
      },
    }
  );

  const [, updateCollateral] = useMutationNotificationWrapper(
    useUpdateCollateralStatus(),
    {
      success: {
        message: "Collateral has been successfully updated.",
      },
    }
  );

  const transactionHistoryData: TransactionHistory[] | undefined | null =
    data?.transactionHistory?.data;

  const pageCount = getPagesCount(data?.transactionHistory?.total);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleOnSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getTransactionHistoryRequestApiSort(sort));
    },
    [setOrderBy]
  );

  const handleOpenEditModal = (type: ModalType, value: TransactionHistory) => {
    setSelectedLimitRequestId(value.limit?.limitRequest?.id);
    if (type === ModalType.ConditionPrecedents) {
      setSelectedConditionalPrecedents(value.conditionPrecedents);
      return conditionPrecedenceEditModalOpen();
    }

    if (type === ModalType.Exceptions) {
      setSelectedExceptions(value.exceptions);
      return exceptionEditModalOpen();
    }
  };

  const handleCloseConditionPrecedenceModal = () => {
    setSelectedConditionalPrecedents(null);
    setSelectedLimitRequestId(null);
    conditionPrecedenceEditModalClose();
  };

  const handleCloseExceptionModal = () => {
    setSelectedExceptions(null);
    setSelectedLimitRequestId(null);
    exceptionEditModalClose();
  };

  const handleOpenCollateralModal = useCallback(
    (value: TransactionHistory) => {
      setSelectedTransactionHistory(value);
      collateralModalOpen();
    },
    [collateralModalOpen]
  );

  const handleUpdate = useCallback(
    (id: number, input: UpdateTransactionHistoryInput) => {
      updateTransactionHistory({ id, input }).then(refresh);
    },
    [updateTransactionHistory, refresh]
  );

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilter((prevFilter) => {
      return { ...prevFilter, [key]: value };
    });
  }, []);

  const handleOpenApproveConfirmation = useCallback(
    (id: number) => {
      setSelectedTransactionHistory(
        transactionHistoryData?.find((item) => item.id === id)
      );
      openApproveConfirmationModal();
    },
    [openApproveConfirmationModal, transactionHistoryData]
  );

  const handleCloseApproveConfirmation = useCallback(() => {
    closeApproveConfirmationModal();
    setSelectedTransactionHistory(null);
  }, [closeApproveConfirmationModal]);

  const handleOpenRejectConfirmation = useCallback(
    (id: number) => {
      setSelectedTransactionHistory(
        transactionHistoryData?.find((item) => item.id === id)
      );
      openRejectConfirmationModal();
    },
    [openRejectConfirmationModal, transactionHistoryData]
  );

  const handleCloseRejectConfirmation = useCallback(() => {
    closeRejectConfirmationModal();
    setSelectedTransactionHistory(null);
  }, [closeRejectConfirmationModal]);

  const handleApprove = useCallback(() => {
    if (!selectedTransactionHistory) {
      return;
    }
    approveTransactionHistory({ id: selectedTransactionHistory.id }).then(
      () => {
        refresh();
        handleCloseApproveConfirmation();
      }
    );
  }, [
    approveTransactionHistory,
    handleCloseApproveConfirmation,
    refresh,
    selectedTransactionHistory,
  ]);

  const handleReject = useCallback(
    (notes: string) => {
      if (!selectedTransactionHistory) {
        return;
      }
      rejectTransactionHistory({
        id: selectedTransactionHistory.id,
        notes,
      }).then(() => {
        refresh();
        handleCloseRejectConfirmation();
      });
    },
    [
      selectedTransactionHistory,
      rejectTransactionHistory,
      refresh,
      handleCloseRejectConfirmation,
    ]
  );

  const [columns, rows] = useTransactionHistoryTableData({
    data: transactionHistoryData,
    canEdit,
    onApprove: handleOpenApproveConfirmation,
    onReject: handleOpenRejectConfirmation,
    onOpenEditModal: handleOpenEditModal,
    onInlineEdit: handleUpdate,
    onChangeCollateral: handleOpenCollateralModal,
  });

  const handleCloseCollateralModal = useCallback(() => {
    collateralModalClose();
    setSelectedTransactionHistory(null);
  }, [collateralModalClose]);

  const handleSaveCollateral = useCallback(
    (collateral: UpdateCollateralInput) => {
      const collateralId = selectedCollateral?.id;
      if (!selectedTransactionHistory || !collateralId) {
        return;
      }
      updateCollateral({
        id: collateralId,
        input: collateral,
      }).then(refresh);
    },
    [
      refresh,
      selectedCollateral?.id,
      selectedTransactionHistory,
      updateCollateral,
    ]
  );

  return (
    <Flex direction="column" gap={16}>
      <SimpleGrid
        w="100%"
        cols={5}
        breakpoints={[
          { maxWidth: "sm", cols: 2 },
          { maxWidth: "lg", cols: 4 },
        ]}
        spacing={20}
        verticalSpacing={12}
      >
        <Select
          size="m"
          clearable
          searchable
          label="Sales Name"
          placeholder="Select Sales Name"
          data={getSalesNameOptions(companies)}
          onChange={(value) => handleFilterChange("salesName", value)}
        />
        <Select
          size="m"
          clearable
          searchable
          label="Sales Team"
          placeholder="Select Sales Team"
          data={getSalesTeamOptions(companies)}
          onChange={(value) => handleFilterChange("salesTeam", value)}
        />
        <Select
          size="m"
          clearable
          label="Has Exceptions"
          placeholder="Select if it has exceptions"
          data={boolSelectorOptions}
          onChange={(value) =>
            handleFilterChange("hasException", stringToBoolean(value))
          }
        />
        <Select
          size="m"
          clearable
          label="Is Restructured"
          placeholder="Select if it is Restructured"
          data={boolSelectorOptions}
          onChange={(value) =>
            handleFilterChange("isRestructured", stringToBoolean(value))
          }
        />
        <Select
          size="m"
          clearable
          label="Order Status"
          placeholder="Select Order Status"
          data={orderStatusOptions}
          onChange={(value: TransactionHistoryOrderStatus) =>
            handleFilterChange("orderStatus", value)
          }
        />
        <Select
          size="m"
          clearable
          label="Payment Type"
          placeholder="Select Payment Type"
          data={paymentTypeOptions}
          onChange={(value) => handleFilterChange("paymentType", value)}
        />
        <Select
          size="m"
          clearable
          label="Payment Method"
          placeholder="Select Payment Method"
          data={paymentMethodOptions}
          onChange={(value) => handleFilterChange("paymentMethod", value)}
        />
        <TextInput
          size="m"
          label="Quotation No"
          placeholder="Quotation No"
          onChange={(event) => {
            setQuotationNoFilter(event.target.value);
          }}
        />
      </SimpleGrid>
      <Table
        columns={columns}
        rows={rows}
        verticalAlignment="top"
        defaultSort={{
          columnAccessor: "orderDate",
          direction: SortDirection.desc,
        }}
        loading={fetching}
        pinLastColumn={canEdit}
        pagination={{
          pageCount: pageCount,
          page: page,
          onPageChange: handlePageChange,
        }}
        onSortChange={handleOnSortChange}
      />
      {showConditionPrecedentEditModal && (
        <ConditionPrecedentsModal
          limitRequestId={selectedLimitRequestId}
          data={selectedConditionPrecedents}
          opened={true}
          onClose={() => {
            handleCloseConditionPrecedenceModal();
            refresh();
          }}
        />
      )}
      {showExceptionEditModal && (
        <ExceptionModal
          limitRequestId={selectedLimitRequestId}
          data={selectedExceptions}
          opened={true}
          onClose={() => {
            handleCloseExceptionModal();
            refresh();
          }}
        />
      )}
      {showApproveConfirmationModal && (
        <ConfirmationModal
          opened={true}
          message={
            <>
              Are you sure you want to APPROVE the transaction for{" "}
              {customer?.name}, {selectedTransactionHistory?.quotationNo}, with
              the total amount of{" "}
              {toCurrencyPrice(selectedTransactionHistory?.totalOrderAmount)}?{" "}
              <br />
              This process is Irreversible.
              <br />
              <br />
              {selectedTransactionHistory?.limitUsed && (
                <>
                  Remaining Limit:{" "}
                  {toCurrencyPrice(
                    selectedTransactionHistory.limitUsed.remaining
                  )}
                </>
              )}
            </>
          }
          onConfirm={handleApprove}
          onClose={handleCloseApproveConfirmation}
        />
      )}
      {showRejectConfirmationModal && (
        <ModalWithNotes
          opened={true}
          buttonTitle="Reject"
          message={
            <>
              Are you sure you want to REJECT the transaction for{" "}
              {customer?.name}, {selectedTransactionHistory?.quotationNo}, with
              the total amount of{" "}
              {toCurrencyPrice(selectedTransactionHistory?.totalOrderAmount)}?
              <br />
              This process is Irreversible, no changes can be made on the
              transaction after this.
              <br />
              <br />
              {selectedTransactionHistory?.limitUsed && (
                <>
                  Remaining Limit:{" "}
                  {toCurrencyPrice(
                    selectedTransactionHistory.limitUsed.remaining
                  )}
                </>
              )}
            </>
          }
          onSubmit={handleReject}
          onClose={handleCloseRejectConfirmation}
        />
      )}
      {showCollateralModal && selectedCollateral && (
        <CollateralModal
          paymentMethod={selectedTransactionHistory.paymentMethod}
          value={selectedCollateral}
          opened={true}
          onClose={handleCloseCollateralModal}
          onSubmit={handleSaveCollateral}
        />
      )}
    </Flex>
  );
};

export default TransactionHistoryList;
